/**
 * Hook: Wallet mint management — add, remove, refresh mints.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { nip19 } from 'nostr-tools';
import type { WalletMintEntry } from '@/lib/storage/db';
import { getAllMints, addMint as storeAddMint, removeMint as storeRemoveMint, updateMintKeysets } from '@/lib/storage/wallet-store';
import { getMintAnnouncementFilter, parseMintAnnouncementEvent } from '@/lib/nostr/mint-announcement';
import type { Keyset } from '@/lib/cashu/keyset';

export function useWalletMints() {
  const [mints, setMints] = useState<WalletMintEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { nostr } = useNostr();

  // Load mints from storage on mount
  useEffect(() => {
    getAllMints().then((stored) => {
      setMints(stored);
      setLoading(false);
    });
  }, []);

  /** Fetch a mint's announcement from Nostr and parse it */
  const fetchMintAnnouncement = useCallback(async (mintPubkey: string): Promise<WalletMintEntry | null> => {
    try {
      const events = await nostr.query([getMintAnnouncementFilter(mintPubkey)], {
        signal: AbortSignal.timeout(15000),
      });
      if (!events || events.length === 0) return null;

      const event = events[0];
      const announcement = parseMintAnnouncementEvent(event);

      return {
        npub: mintPubkey,
        name: announcement.name,
        description: announcement.description,
        keysets: announcement.keysets,
        fees: announcement.fees,
        requiredConfirmations: announcement.requiredConfirmations,
        addedAt: Date.now(),
      };
    } catch (err) {
      console.error('Failed to fetch mint announcement:', err);
      return null;
    }
  }, [nostr]);

  /** Add a mint by its npub (bech32 or hex) */
  const addMint = useCallback(async (input: string): Promise<boolean> => {
    // Decode npub1... bech32 to hex
    let mintPubkey = input.trim();
    if (mintPubkey.startsWith('npub1')) {
      try {
        const decoded = nip19.decode(mintPubkey);
        if (decoded.type !== 'npub') return false;
        mintPubkey = decoded.data as string;
      } catch {
        return false;
      }
    }
    const entry = await fetchMintAnnouncement(mintPubkey);
    if (!entry) return false;

    await storeAddMint(entry);
    setMints((prev) => [...prev, entry]);
    return true;
  }, [fetchMintAnnouncement]);

  /** Remove a mint */
  const removeMint = useCallback(async (npub: string) => {
    await storeRemoveMint(npub);
    setMints((prev) => prev.filter((m) => m.npub !== npub));
  }, []);

  /** Refresh a mint's keysets and info from Nostr */
  const refreshMint = useCallback(async (npub: string) => {
    const entry = await fetchMintAnnouncement(npub);
    if (!entry) return false;

    await updateMintKeysets(npub, entry.keysets);
    setMints((prev) => prev.map((m) => (m.npub === npub ? entry : m)));
    return true;
  }, [fetchMintAnnouncement]);

  return {
    mints,
    loading,
    addMint,
    removeMint,
    refreshMint,
    fetchMintAnnouncement,
  };
}
